/**
 * 无 Vue / 无 vue-i18n 的轻量多语言：语言与 appLocale SSOT 对齐。
 */
import { getAppLocale, toVueI18nKey } from './appLocale'

/** 与业务里 getLangType、文案表约定一致的语言键 */
export type PlainLocaleKey = 'zhCn' | 'zhTc' | 'en'

const { VUE_APP_ENV = 'sit' } = import.meta.env

/** 与 packages/i18n/tools 一致：仅 sit 开放英文 */
export const kEnglishEnabled = VUE_APP_ENV === 'sit'

export type PlainMessages = {
  zhCn: Record<string, unknown>
  zhTc: Record<string, unknown>
  /** 英文可选；未传或关闭英文时走简体 */
  en?: Record<string, unknown>
}

export type CreatePlainTranslatorOptions = {
  /** 是否使用英文表，默认与 kEnglishEnabled（构建环境）一致 */
  enableEnglish?: boolean
  /** 当前语言缺 key 时回落到该语言的表，默认 zhCn */
  fallbackLocale?: PlainLocaleKey
}

/** 文案表使用的语言键，与头部切换一致 */
export function resolveAppLocale(enableEnglish: boolean = kEnglishEnabled): PlainLocaleKey {
  const key = toVueI18nKey(getAppLocale())
  if (key === 'en' && !enableEnglish) return 'zhCn'
  return key
}

/**
 * 点分路径取值，如 requestCode.dlsxqcxdl
 */
export function getMessageByPath(
  obj: Record<string, unknown> | undefined,
  path: string
): string | undefined {
  if (obj == null) return undefined
  const parts = path.split('.')
  let cur: unknown = obj
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[p]
  }
  return typeof cur === 'string' ? cur : undefined
}

function pickMessageBundle(
  locale: PlainLocaleKey,
  messages: PlainMessages,
  enableEnglish: boolean
): Record<string, unknown> {
  if (locale === 'en' && enableEnglish && messages.en) return messages.en as Record<string, unknown>
  if (locale === 'zhTc') return messages.zhTc
  return messages.zhCn
}

function pickFallbackBundle(
  fallbackLocale: PlainLocaleKey,
  messages: PlainMessages
): Record<string, unknown> {
  if (fallbackLocale === 'zhTc') return messages.zhTc
  if (fallbackLocale === 'en' && messages.en) return messages.en as Record<string, unknown>
  return messages.zhCn
}

/**
 * 根据多语言数据创建 t(key)，语言与 getAppLocale() 对齐。
 *
 * @example
 * const t = createPlainTranslator({ zhCn, zhTc, en: enUs })
 * t('requestCode.dlsxqcxdl')
 */
export function createPlainTranslator(
  messages: PlainMessages,
  options?: CreatePlainTranslatorOptions
): (key: string) => string {
  const enableEnglish = options?.enableEnglish ?? kEnglishEnabled
  const fallbackLocale = options?.fallbackLocale ?? 'zhCn'

  return (key: string) => {
    const locale = resolveAppLocale(enableEnglish)
    const bundle = pickMessageBundle(locale, messages, enableEnglish)
    let text = getMessageByPath(bundle, key)
    if (text == null && locale !== fallbackLocale) {
      text = getMessageByPath(pickFallbackBundle(fallbackLocale, messages), key)
    }
    return text ?? key
  }
}
